import { cn } from "@/lib/utils";
import React, { forwardRef, HTMLAttributes } from "react";

interface AvatarProps extends HTMLAttributes<HTMLDivElement> {
  size?: "sm" | "md" | "lg" | "xl";
  src?: string | undefined;
  alt?: string;
  fallback?: string;
  status?: "online" | "offline" | "away" | "busy";
}

const Avatar = forwardRef<HTMLDivElement, AvatarProps>(
  ({ className, size = "md", src, alt, fallback, status, ...props }, ref) => {
    const sizes = {
      sm: "h-8 w-8 text-xs",
      md: "h-10 w-10 text-sm",
      lg: "h-12 w-12 text-base",
      xl: "h-16 w-16 text-lg",
    };

    const statusSizes = {
      sm: "h-2 w-2",
      md: "h-2.5 w-2.5",
      lg: "h-3 w-3",
      xl: "h-4 w-4",
    };

    const statusColors = {
      online: "bg-success",
      offline: "bg-text-tertiary",
      away: "bg-warning",
      busy: "bg-error",
    };

    // Generate initials from fallback text
    const getInitials = (name: string) => {
      return name
        .split(" ")
        .map((word) => word.charAt(0))
        .join("")
        .toUpperCase()
        .slice(0, 2);
    };

    return (
      <div
        ref={ref}
        className={cn("relative inline-block", className)}
        {...props}
      >
        <div
          className={cn(
            "flex items-center justify-center rounded-full bg-background-tertiary border border-border-light overflow-hidden",
            sizes[size]
          )}
        >
          {src ? (
            <img
              src={src}
              alt={alt || "Avatar"}
              className="h-full w-full object-cover"
              onError={(e) => {
                // Hide image on error, fallback will show
                e.currentTarget.style.display = "none";
              }}
            />
          ) : (
            <span className="font-medium text-text-secondary">
              {fallback ? getInitials(fallback) : "?"}
            </span>
          )}
        </div>

        {status && (
          <span
            className={cn(
              "absolute bottom-0 right-0 rounded-full border-2 border-background-secondary",
              statusSizes[size],
              statusColors[status]
            )}
          />
        )}
      </div>
    );
  }
);

Avatar.displayName = "Avatar";

interface AvatarGroupProps extends HTMLAttributes<HTMLDivElement> {
  max?: number;
  size?: "sm" | "md" | "lg" | "xl";
  children: React.ReactElement<AvatarProps>[];
}

const AvatarGroup = forwardRef<HTMLDivElement, AvatarGroupProps>(
  ({ className, max = 3, size = "md", children, ...props }, ref) => {
    const visibleAvatars = children.slice(0, max);
    const hiddenCount = Math.max(0, children.length - max);

    const spacingClasses = {
      sm: "-space-x-1",
      md: "-space-x-2",
      lg: "-space-x-2",
      xl: "-space-x-3",
    };

    return (
      <div
        ref={ref}
        className={cn("flex items-center", spacingClasses[size], className)}
        {...props}
      >
        {visibleAvatars.map((avatar, index) => (
          <div
            key={index}
            className="ring-2 ring-background-secondary rounded-full"
          >
            {React.cloneElement(avatar, { size })}
          </div>
        ))}

        {hiddenCount > 0 && (
          <div className="ring-2 ring-background-secondary rounded-full">
            <Avatar
              size={size}
              fallback={`+${hiddenCount}`}
              className="bg-background-tertiary"
            />
          </div>
        )}
      </div>
    );
  }
);

AvatarGroup.displayName = "AvatarGroup";

export { Avatar, AvatarGroup };
